/* Macsolve storefront behaviour. Vanilla ES2020, no dependencies. */

/* Analytics: whatsapp_order_click (SPEC section 10). No hard dependency on any tracker. */
const msTrack = (event, params) => {
  if (Array.isArray(window.dataLayer)) window.dataLayer.push({ event, ...params });
  if (typeof window.gtag === 'function') window.gtag('event', event, params);
  if (event === 'whatsapp_order_click' && typeof window.fbq === 'function') window.fbq('track', 'Contact');
};

document.addEventListener('click', (event) => {
  const link = event.target.closest('[data-wa-source]');
  if (!link) return;
  const holder = link.closest('[data-product-id]');
  const params = { source: link.dataset.waSource, currency: document.querySelector('[data-currency]')?.dataset.currency || 'INR' };
  if (holder) params.product_id = holder.dataset.productId;
  if (link.dataset.waValue) params.value = Number(link.dataset.waValue);
  msTrack('whatsapp_order_click', params);
});

/* WhatsApp forms (contact, service enquiry): fields marked data-wa-label are composed into one wa.me text under the
   field named "text". Without JS only that field is sent. */
document.addEventListener('submit', (event) => {
  const form = event.target.closest('[data-wa-contact]');
  if (!form) return;
  const field = form.querySelector('[name="text"]');
  const lines = [form.dataset.intro];
  form.querySelectorAll('[data-wa-label]').forEach((input) => {
    const value = input.type === 'checkbox' ? (input.checked ? input.dataset.waYes : '') : input.value.trim();
    if (value) lines.push(`${input.dataset.waLabel}: ${value}`);
  });
  lines.push('', field.value.trim());
  /* Swap in the composed text for the submit only, so a back navigation keeps what was typed */
  const original = field.value;
  field.value = lines.join('\n');
  setTimeout(() => { field.value = original; });
  msTrack('whatsapp_order_click', { source: form.dataset.waSource || 'contact', currency: 'INR' });
  msTrack('generate_lead', { source: form.dataset.waSource || 'contact' });
});

const msWaUrl = (number, text) => `https://wa.me/${number}?text=${encodeURIComponent(text)}`;

const msFormatMoney = (cents, format) => {
  const amount = (cents / 100).toLocaleString('en-IN', { maximumFractionDigits: cents % 100 === 0 ? 0 : 2 });
  return format ? format.replace(/\{\{\s*\w+\s*\}\}/, amount) : `₹${amount}`;
};

/* Product WhatsApp button: follows variant:change { detail: { id, title, price, available } } */
class WhatsappOrder extends HTMLElement {
  connectedCallback() {
    this.onVariant = (event) => this.update(event.detail || {});
    document.addEventListener('variant:change', this.onVariant);
  }

  disconnectedCallback() {
    document.removeEventListener('variant:change', this.onVariant);
  }

  update({ id, title, price, available }) {
    const button = this.querySelector('a.ms-wa__button');
    if (!id || !button || available === false) return;
    const { intro, optionLabel, priceLabel, number, url } = this.dataset;
    const lines = [intro, `*${this.dataset.title}*`];
    if (title && title !== 'Default Title') lines.push(`${optionLabel}: ${title}`);
    if (price) lines.push(`${priceLabel}: ${price}`);
    lines.push(`${url}?variant=${id}`);
    button.href = msWaUrl(number, lines.join('\n'));
  }
}
customElements.define('whatsapp-order', WhatsappOrder);

/* Cart WhatsApp button: rebuilds the message from /cart.js on click so it is always current */
class WhatsappCart extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', (event) => this.onClick(event));
  }

  async onClick(event) {
    const button = event.target.closest('a.ms-wa__button');
    if (!button) return;
    event.preventDefault();
    const win = window.open('', '_blank');
    if (win) win.opener = null;
    let href = button.href;
    try {
      /* Wait for a quantity or order note that is still being saved (published by cart-drawer.js) */
      await window.msCartPending;
      const cart = await (await fetch(`${window.Shopify?.routes?.root || '/'}cart.js`)).json();
      const { intro, totalLabel, noteLabel, moneyFormat, number } = this.dataset;
      const lines = [intro, ''];
      cart.items.forEach((item, index) => {
        const option = item.variant_title && item.variant_title !== 'Default Title' ? ` (${item.variant_title})` : '';
        lines.push(`${index + 1}. ${item.product_title}${option} x ${item.quantity} - ${msFormatMoney(item.final_line_price, moneyFormat)}`);
        lines.push(`   ${location.origin}${item.url}`);
      });
      lines.push('', `${totalLabel}: ${msFormatMoney(cart.total_price, moneyFormat)}`);
      if (cart.note) lines.push('', `${noteLabel}: ${cart.note}`);
      href = msWaUrl(number, lines.join('\n'));
    } catch (error) {
      /* Fall back to the server-rendered message */
    }
    if (win) win.location.href = href;
    else window.location.href = href;
  }
}
customElements.define('whatsapp-cart', WhatsappCart);

/* Header: mobile drawer and desktop mega-menu built on <details>, with Escape and focus return */
class MsDisclosure extends HTMLElement {
  connectedCallback() {
    this.details = this.querySelector('details');
    if (!this.details) return;
    this.summary = this.details.querySelector('summary');
    this.isDrawer = this.hasAttribute('data-drawer');
    this.details.addEventListener('toggle', () => this.onToggle());
    this.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.details.open) {
        event.stopPropagation();
        this.close();
      }
    });
    if (!this.isDrawer) {
      document.addEventListener('click', (event) => {
        if (this.details.open && !this.contains(event.target)) this.close(false);
      });
      this.addEventListener('focusout', (event) => {
        if (this.details.open && !this.contains(event.relatedTarget)) this.close(false);
      });
    }
    this.querySelectorAll('[data-close]').forEach((button) => button.addEventListener('click', () => this.close()));
    if (this.isDrawer) {
      /* Leaving through a menu link, or coming back from the browser cache, must never leave the drawer and its backdrop open */
      this.querySelectorAll('[data-panel] a[href]').forEach((link) =>
        link.addEventListener('click', () => {
          if (!link.target) this.close(false);
        })
      );
      window.addEventListener('pageshow', () => {
        if (this.details.open) this.close(false);
        document.body.classList.remove('ms-scroll-lock');
      });
    }
  }

  onToggle() {
    this.summary.setAttribute('aria-expanded', String(this.details.open));
    if (this.isDrawer) {
      document.body.classList.toggle('ms-scroll-lock', this.details.open);
      if (this.details.open) {
        this.trap = (event) => this.trapFocus(event);
        document.addEventListener('keydown', this.trap);
        requestAnimationFrame(() => this.querySelector('[data-close]')?.focus());
      } else {
        document.removeEventListener('keydown', this.trap);
      }
    }
  }

  trapFocus(event) {
    if (event.key !== 'Tab') return;
    const panel = this.details.querySelector('[data-panel]');
    const focusable = [...panel.querySelectorAll('a[href], button, summary, input')].filter((el) => el.offsetParent !== null);
    if (!focusable.length) return;
    const first = focusable[0];
    const last = focusable[focusable.length - 1];
    if (event.shiftKey && document.activeElement === first) {
      event.preventDefault();
      last.focus();
    } else if (!event.shiftKey && document.activeElement === last) {
      event.preventDefault();
      first.focus();
    }
  }

  close(returnFocus = true) {
    this.details.open = false;
    if (returnFocus) this.summary.focus();
  }
}
customElements.define('ms-disclosure', MsDisclosure);

/* Sticky header: desktop sticks only the menu section at the top; mobile slides the bar */
(() => {
  const header = document.querySelector('[data-sticky-header]');
  if (!header) return;
  const section = header.closest('.ms-header-section') || header;
  const nav = header.querySelector('.ms-header__nav');

  const updateOffset = () => {
    if (nav && window.innerWidth >= 990) {
      const offset = nav.offsetTop;
      section.style.setProperty('--ms-header-top-offset', `${offset}px`);
    } else {
      section.style.setProperty('--ms-header-top-offset', '0px');
    }
  };

  updateOffset();
  window.addEventListener('resize', updateOffset, { passive: true });
  if (window.ResizeObserver) {
    new ResizeObserver(updateOffset).observe(header);
  }

  let last = window.scrollY;
  let ticking = false;
  window.addEventListener(
    'scroll',
    () => {
      if (ticking) return;
      ticking = true;
      requestAnimationFrame(() => {
        const y = window.scrollY;
        const isDesktop = window.innerWidth >= 990;
        const open = header.querySelector('details[open]');
        const topOffset = parseFloat(section.style.getPropertyValue('--ms-header-top-offset')) || 0;

        if (!isDesktop) {
          header.classList.toggle('ms-header--hidden', !open && y > last && y > 160);
          header.classList.toggle('ms-header--scrolled', y > 8);
        } else {
          header.classList.remove('ms-header--hidden');
          const isStuck = y >= topOffset && topOffset > 0;
          header.classList.toggle('ms-header--scrolled', isStuck);
          section.classList.toggle('is-stuck', isStuck);
        }

        last = y;
        ticking = false;
      });
    },
    { passive: true }
  );
})();

const msMotionOk = () => !window.matchMedia('(prefers-reduced-motion: reduce)').matches;

/* Product carousel: prev/next scroll one view, progress dots follow the scroll position */
class MsCarousel extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    if (!this.track) return;
    this.prev = this.querySelector('[data-prev]');
    this.next = this.querySelector('[data-next]');
    this.dots = this.querySelector('[data-dots]');
    this.prev?.addEventListener('click', () => this.page(-1));
    this.next?.addEventListener('click', () => this.page(1));
    this.track.addEventListener('scroll', () => this.queue(), { passive: true });
    this.resizer = new ResizeObserver(() => this.update());
    this.resizer.observe(this.track);
  }

  disconnectedCallback() {
    this.resizer?.disconnect();
  }

  page(direction) {
    const button = direction < 0 ? this.prev : this.next;
    if (button?.getAttribute('aria-disabled') === 'true') return;
    this.track.scrollBy({ left: direction * this.track.clientWidth, behavior: msMotionOk() ? 'smooth' : 'auto' });
  }

  queue() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.update();
    });
  }

  update() {
    const { scrollLeft, scrollWidth, clientWidth } = this.track;
    const max = scrollWidth - clientWidth;
    const scrollable = max > 4;
    this.toggleAttribute('data-scrollable', scrollable);
    this.prev?.setAttribute('aria-disabled', String(!scrollable || scrollLeft <= 4));
    this.next?.setAttribute('aria-disabled', String(!scrollable || scrollLeft >= max - 4));
    if (!this.dots) return;
    const pages = scrollable ? Math.ceil(max / clientWidth) + 1 : 0;
    if (this.dots.childElementCount !== pages) {
      this.dots.replaceChildren(...Array.from({ length: pages }, () => document.createElement('span')));
      [...this.dots.children].forEach((dot) => dot.classList.add('ms-carousel__dot'));
    }
    const current = scrollable ? Math.round((scrollLeft / max) * (pages - 1)) : 0;
    [...this.dots.children].forEach((dot, index) => dot.classList.toggle('ms-carousel__dot--active', index === current));
  }
}
customElements.define('ms-carousel', MsCarousel);

/* Tabs: ARIA tabs pattern with arrow, Home and End keys; panels are server-rendered */
class MsTabs extends HTMLElement {
  connectedCallback() {
    this.tabs = [...this.querySelectorAll('[role="tab"]')];
    this.tabs.forEach((tab, index) => {
      tab.addEventListener('click', () => this.select(index));
      tab.addEventListener('keydown', (event) => this.onKey(event, index));
    });
  }

  select(index, focus = false) {
    this.tabs.forEach((tab, i) => {
      const selected = i === index;
      tab.setAttribute('aria-selected', String(selected));
      tab.tabIndex = selected ? 0 : -1;
      const panel = document.getElementById(tab.getAttribute('aria-controls'));
      if (panel) panel.hidden = !selected;
    });
    if (focus) this.tabs[index].focus();
  }

  onKey(event, index) {
    const last = this.tabs.length - 1;
    const targets = { ArrowRight: index === last ? 0 : index + 1, ArrowLeft: index === 0 ? last : index - 1, Home: 0, End: last };
    if (!(event.key in targets)) return;
    event.preventDefault();
    this.select(targets[event.key], true);
  }
}
customElements.define('ms-tabs', MsTabs);

/* Hero slider: one slide per view, dots and arrows, optional autoplay that pauses on hover/focus */
class MsSlider extends HTMLElement {
  connectedCallback() {
    this.track = this.querySelector('[data-track]');
    this.slides = [...this.querySelectorAll('[data-slide]')];
    if (!this.track || this.slides.length < 2) return;
    this.dots = [...this.querySelectorAll('[data-dot]')];
    this.index = 0;
    this.querySelector('[data-prev]')?.addEventListener('click', () => this.go(this.index - 1));
    this.querySelector('[data-next]')?.addEventListener('click', () => this.go(this.index + 1));
    this.dots.forEach((dot, i) => dot.addEventListener('click', () => this.go(i)));
    this.track.addEventListener('scroll', () => this.queue(), { passive: true });
    this.sync();

    this.pauseButton = this.querySelector('[data-pause]');
    this.playing = this.hasAttribute('data-autoplay') && msMotionOk();
    if (!this.playing) {
      this.pauseButton?.setAttribute('hidden', '');
      return;
    }
    this.delay = Number(this.dataset.interval || 6) * 1000;
    this.pauseButton?.addEventListener('click', () => {
      this.playing = !this.playing;
      this.pauseButton.setAttribute('aria-pressed', String(!this.playing));
      this.playing ? this.start() : this.stop();
    });
    this.addEventListener('mouseenter', () => this.stop());
    this.addEventListener('mouseleave', () => this.playing && this.start());
    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', (event) => {
      if (this.playing && !this.contains(event.relatedTarget)) this.start();
    });
    this.start();
  }

  disconnectedCallback() {
    this.stop();
  }

  start() {
    this.stop();
    this.timer = setInterval(() => this.go(this.index + 1), this.delay);
  }

  stop() {
    clearInterval(this.timer);
  }

  go(index) {
    const count = this.slides.length;
    this.index = (index + count) % count;
    this.track.scrollTo({ left: this.index * this.track.clientWidth, behavior: msMotionOk() ? 'smooth' : 'auto' });
  }

  queue() {
    if (this.ticking) return;
    this.ticking = true;
    requestAnimationFrame(() => {
      this.ticking = false;
      this.index = Math.round(this.track.scrollLeft / this.track.clientWidth);
      this.sync();
    });
  }

  /* Off-screen slides are inert so keyboard focus never lands on hidden content */
  sync() {
    this.slides.forEach((slide, i) => {
      slide.inert = i !== this.index;
    });
    this.dots.forEach((dot, i) => {
      if (i === this.index) dot.setAttribute('aria-current', 'true');
      else dot.removeAttribute('aria-current');
    });
  }
}
customElements.define('ms-slider', MsSlider);

/* The floating WhatsApp button steps aside while the hero's own WhatsApp action is on screen */
{
  const heroAction = document.querySelector('[data-hero-wa]');
  const floating = document.querySelector('.ms-wa-float');
  if (heroAction && floating && 'IntersectionObserver' in window) {
    new IntersectionObserver(([entry]) => {
      floating.classList.toggle('ms-wa-float--hidden', entry.isIntersecting);
    }).observe(heroAction);
  }
}

/* Promo countdown: counts down to the merchant's real end date; hides itself once the date has passed */
class MsCountdown extends HTMLElement {
  connectedCallback() {
    this.end = Date.parse(this.dataset.end);
    if (Number.isNaN(this.end)) return;
    this.tick();
    this.timer = setInterval(() => this.tick(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.timer);
  }

  tick() {
    const left = this.end - Date.now();
    if (left <= 0) {
      clearInterval(this.timer);
      this.hidden = true;
      return;
    }
    const parts = {
      days: Math.floor(left / 86400000),
      hours: Math.floor(left / 3600000) % 24,
      mins: Math.floor(left / 60000) % 60,
      secs: Math.floor(left / 1000) % 60,
    };
    Object.entries(parts).forEach(([unit, value]) => {
      const target = this.querySelector(`[data-unit="${unit}"]`);
      if (target) target.textContent = String(value).padStart(2, '0');
    });
  }
}
customElements.define('ms-countdown', MsCountdown);

/* Search results page: tell analytics what was searched and how many results came back */
{
  const results = document.querySelector('[data-search-results]');
  if (results && results.dataset.term) {
    msTrack('search', { search_term: results.dataset.term, results_count: Number(results.dataset.total) });
  }
}
